import { useMemo } from 'react';
import Skeleton from '@material-ui/lab/Skeleton';
import formatCurrency from '../lib/formatCurrency';

function formatRanking(value) {
  return (
    <>
      <span className="tw-opacity-50">#</span>
      {value.toLocaleString()}
    </>
  );
}

export default function DomainMetrics({ loading, presenter }) {
  const metrics = useMemo(() => {
    return [
      {
        format: value => value.toLocaleString(),
        key: 'extensionsTaken',
        label: 'Number of extensions registered with this keyword',
        name: 'Extensions Taken',
        value: presenter.extensionsTaken,
      },
      {
        key: 'yearRegistered',
        label: presenter.ageYearsFormatted,
        name: 'Year Registered',
        value: presenter.yearRegistered,
      },
      {
        format: value => formatCurrency(value, 0),
        key: 'estibotValue',
        label: 'Ballpark third-party appraisal',
        name: 'Estibot Value',
        value: presenter.estibotValue,
        visible: (presenter.estibotValue ?? 0) >= 500,
      },
      {
        format: formatRanking,
        key: 'alexaRanking',
        label: 'Ranking signals significant traffic',
        name: 'Alexa',
        value: presenter.alexaRanking,
      },
      {
        format: formatRanking,
        key: 'umbrellaRanking',
        label: 'Ranking signals significant traffic',
        name: 'Umbrella',
        value: presenter.umbrellaRanking,
      },
      {
        format: value => value.toLocaleString(),
        key: 'pageRank',
        label: 'on Google',
        name: 'Page Rank',
        value: presenter.pageRank,
        visible: (presenter.pageRank ?? 0) > 1,
      },
      {
        format: value => value.toLocaleString(),
        key: 'keywordSearchCount',
        name: 'Keyword Search',
        label: (
          <>
            <span className="tw-font-medium">
              “{presenter.keywordSearchQuery}”
            </span>{' '}
            receives this many searches per month on Google
          </>
        ),
        value: presenter.keywordSearchCount,
      },
      {
        format: value => value.toLocaleString(),
        key: 'ahrefsDomainRating',
        label: 'SEO ranking out of 100',
        name: 'Ahrefs Domain Rating',
        value: presenter.ahrefsDomainRating,
      },
      {
        format: value => value.toLocaleString(),
        key: 'backlinksCount',
        label: 'Number of external domains linking back',
        name: 'Backlinks',
        value: presenter.backlinksCount,
        visible: (presenter.backlinksCount ?? 0) > 0,
      },
      {
        key: 'prevSold',
        name: 'Previously sold in',
        label: presenter.soldPrice
          ? `sold for ${formatCurrency(presenter.soldPrice)} (estimated)`
          : '',
        value: presenter.soldYear,
      },
      {
        key: 'nameLength',
        label: 'Number of characters in the second-level domain',
        name: 'Name Length',
        value: presenter.nameLength,
      },
    ].filter(({ value, visible = true }) => visible && Boolean(value));
  }, [presenter]);
  if (metrics.length === 0) return null;
  return (
    <div className="tw-grid tw-gap-3 sm:tw-gap-4 tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-2 xl:tw-grid-cols-3">
      {metrics.map(metric => (
        <DomainMetric {...metric} loading={loading} />
      ))}
    </div>
  );
}

const DomainMetric = ({
  format = value => value,
  label = '',
  loading = false,
  name = '',
  value = '',
}) => {
  const Component = loading ? Skeleton : 'div';
  return (
    <Component
      className="tw-relative tw-domain-metric-cols md:tw-domain-metric-rows lg:tw-domain-metric-cols xl:tw-domain-metric-rows tw-flex tw-py-8 tw-max-w-none tw-text-center tw-rounded-lg"
      variant="rect"
      style={{
        color: 'rgba(0, 0, 0, 0.6)',
      }}>
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-px-4 tw-mx-auto">
        <div className="tw-relative tw-uppercase tw-text-sm tw-font-bold tw-leading-snug">
          {name}
        </div>
        {value && (
          <div
            className="tw-flex tw-items-center tw-justify-center tw-mt-2 tw-mb-3 tw-py-2 tw-px-4 tw-rounded-lg tw-text-2xl tw-font-black tw-leading-none tw-break-words tw-opacity-90"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.4)' }}>
            {format(value)}
          </div>
        )}
        <div className="tw-relative tw-text-xs tw-leading-snug">{label}</div>
      </div>
    </Component>
  );
};
